import { useState } from 'react'
import { Alert, App, Button, Divider, Form, Input, Modal, Space, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import type { OperationResult } from '@/shared/api/operationResult'
import { Can } from '@/shared/auth/Can'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  CAN_MARK_FOUND,
  CAN_MARK_LOST,
  decommissionEquipment,
  foundEquipment,
  markEquipmentFaulty,
  markEquipmentLost,
  repairEquipment,
  unassignEquipment,
  type Equipment,
  type EquipmentState,
} from './equipmentApi'

interface EquipmentActionsProps {
  equipment: Equipment
  /** Amal muvaffaqiyatli tugaganda — kartochkani yopish uchun */
  onDone?: () => void
  /**
   * Egani tayinlash/o'zgartirish oynasini ochish.
   *
   * ⚠️ Oyna SHU YERDA chizilmaydi — uni sahifa boshqaradi. Sabab: aynan shu
   * oyna tahrirlash oynasidan ham ochiladi (XSB-003 hisoboti "Edit oynasida
   * Egasi maydoni yo'q" deydi), ya'ni ikki chaqiruvchisi bor. Ikki nusxa
   * qilsak, ulardan biri jimgina eskirardi.
   */
  onChangeOwner?: () => void
}

/**
 * Jihozning hayotiy sikli amallari.
 *
 * <h2>Qaysi tugma qachon</h2>
 * Ro'yxat HOLATGA qarab o'zgaradi, chunki o'tishlar serverdagi holat
 * mashinasi bilan cheklangan: omborda turgan jihozni "ta'mirlab" bo'lmaydi,
 * hisobdan chiqarilganini esa umuman qo'zg'atib bo'lmaydi. Bajarilmaydigan
 * tugmani ko'rsatib, keyin serverdan xato olish operatorni chalg'itardi.
 *
 * <h2>⚠️ ESKI IZOH BEKOR QILINDI (2026-08-13, XSB-003)</h2>
 * Bu yerda «"Biriktirish" tugmasi yo'q» deb yozilgan edi: egasi boshqa
 * moduldagi yozuv, uni tanlash uchun alohida tanlagich kerak, "id ni qo'lda
 * yozing" maydoni esa yaroqsiz biriktirishlarni ochib berardi.
 *
 * Dalil TO'G'RI edi va o'zgarmadi — xulosa o'zgardi: tanlagich yozildi
 * (`ownerApi.ts`, `EquipmentOwnerModal.tsx`) va tugma qo'shildi. Tashqi
 * tekshiruvchi aynan shu bo'shliqni topdi: kartochkada "Egasi: tayinlanmagan"
 * ko'rinardi, lekin uni tayinlaydigan joy yo'q edi.
 *
 * ⚠️ Tugma HOLATGA qarab o'zgaradi va sabab serverda: `assign` faqat
 * biriktirilmagan jihozga o'tadi, `ASSIGNED` da esa `AlreadyAssigned`
 * qaytadi. Shuning uchun `ASSIGNED` holatida "Egasini o'zgartirish"
 * ko'rsatiladi va u ikki qadamda bajariladi (`EquipmentOwnerModal` izohi).
 *
 * <h2>⚠️ TUGMALAR IKKI GURUHGA AJRATILDI (2026-08-14)</h2>
 * Ilgari hammasi bitta qatorda turardi. «Yo'qolgan deb belgilash»
 * qo'shilganda bu joylashuv chidamasdi: `FAULTY` holatida yashil
 * «Ta'mirlangan deb belgilash» bilan qizil «Hisobdan chiqarish» yonma-yon
 * tushardi va oradagi masofa bir necha piksel edi.
 *
 * ⚠️ Bu ATAYLAB tuzatildi, tasdiq oynasi qo'shish bilan YOPILMADI. Bugun
 * kabinetda aynan shu dars o'lchandi (`8da60fa`): chiqish ikonkasi parol
 * ikonkasining yonida turardi, tasdiq oynasi ALLAQACHON bor edi va baribir
 * "yonidagisiga tegib ketish juda oson" bo'lib qolardi — yechim tasdiqni
 * kuchaytirish emas, YONMA-YONLIKNI olib tashlash bo'ldi.
 *
 * Endi tartib shunday:
 *   1. odatiy amallar (ega, qaytarish, ta'mirlash, topildi);
 *   2. ajratuvchi chiziq va sarlavha — «Xavfli amallar»;
 *   3. qaytariladigan yomon xabar (nosoz / yo'qoldi) — qizil kontur;
 *   4. YAKUNIY amal (hisobdan chiqarish) — to'ldirilgan qizil, ALOHIDA
 *      qatorda. U hech qachon boshqa tugmaning yonida turmaydi, chunki
 *      undan qaytish yo'q (`DECOMMISSIONED` — yakuniy holat).
 */
export function EquipmentActions({ equipment, onDone, onChangeOwner }: EquipmentActionsProps) {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()

  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<OperationResult | null>(null)
  const [faultyOpen, setFaultyOpen] = useState(false)
  const [lostOpen, setLostOpen] = useState(false)

  /**
   * Amal tugadi — natijani ekranga chiqarish.
   *
   * ⚠️ ALOHIDA funksiya, chunki ikkita chaqiruvchisi bor: tugmalar
   * (`perform`) va o'z mutatsiyasi bilan ishlaydigan `LostModal`. Ikki
   * nusxa qilinsa, ulardan biri jimgina eskirardi — masalan `PARTIAL`
   * javob bir joyda ko'rsatilib, ikkinchisida "bajarildi" bo'lib
   * o'tib ketardi.
   */
  const report = (outcome: OperationResult | null, successMessage: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all })

    // Server holat aytmagan bo'lsa (`null`) — HTTP 2xx va envelope
    // `success` ga ishonamiz, o'z xabarimizni ko'rsatamiz. Aytgan bo'lsa
    // va u OK emas — YASHIRMAYMIZ (`equipmentApi.toResult` izohi).
    if (outcome && outcome.status !== 'OK') {
      setResult(outcome)
      if (outcome.status === 'FAILED') return
    } else {
      message.success(successMessage)
    }
    onDone?.()
  }

  const perform = async (
    action: () => Promise<OperationResult | null>,
    successMessage: string,
  ) => {
    setRunning(true)
    setResult(null)
    try {
      report(await action(), successMessage)
    } catch (error) {
      setResult({ status: 'FAILED', message: translateError(error, t) })
    } finally {
      setRunning(false)
    }
  }

  const confirmDecommission = () => {
    modal.confirm({
      title: t('inventory.decommissionTitle'),
      content: (
        <Space direction="vertical" size={4}>
          <Typography.Text>
            {t('inventory.decommissionSubject', { serial: equipment.serialNumber })}
          </Typography.Text>
          <Typography.Text type="secondary">{t('inventory.decommissionWarn')}</Typography.Text>
        </Space>
      ),
      okText: t('inventory.decommission'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: () =>
        perform(() => decommissionEquipment(equipment.id), t('inventory.decommissionDone')),
    })
  }

  const state = equipment.state

  /*
    ⚠️ Ro'yxatlar `equipmentApi` da va ular BACKENDDAN ko'chirilgan — bu
    yerda `state === '...'` deb qayta yozilmaydi. Sabab o'sha ro'yxatlar
    ustidagi izohda: nusxa ajralib ketsa, tugma ko'rinadi-yu server 409
    qaytaradi, va buni `tests/tester-bugs.mjs` Java manbasi bilan
    solishtirib ushlaydi.
  */
  const canMarkLost = (CAN_MARK_LOST as readonly EquipmentState[]).includes(state)
  const canMarkFound = (CAN_MARK_FOUND as readonly EquipmentState[]).includes(state)
  const canMarkFaulty = state === 'IN_STOCK' || state === 'ASSIGNED'
  const canDecommission = state === 'FAULTY' || state === 'LOST'

  return (
    <>
      {result ? (
        <div style={{ marginBottom: 12 }}>
          <OperationResultAlert
            partialNote={false}
            result={result}
            closable
            onClose={() => setResult(null)}
          />
        </div>
      ) : null}

      {state === 'DECOMMISSIONED' ? (
        <Typography.Text type="secondary">{t('inventory.decommissionedHint')}</Typography.Text>
      ) : (
        <Can permission="inventory.equipment.write">
          <Space wrap>
            {/*
              Egani boshqarish (XSB-003).

              ⚠️ `LOST` va `FAULTY` da tugma YO'Q va bu serverning holat
              mashinasidan kelib chiqadi: ular `ASSIGNED` ga o'ta olmaydi.
              Bosilmaydigan tugmani ko'rsatib, keyin serverdan xato olish
              operatorni chalg'itardi (bu faylning bosh qoidasi).
            */}
            {onChangeOwner && (state === 'IN_STOCK' || state === 'ASSIGNED') ? (
              <Button size="small" type={state === 'IN_STOCK' ? 'primary' : 'default'} onClick={onChangeOwner}>
                {state === 'ASSIGNED' ? t('inventory.ownerChange') : t('inventory.ownerAssign')}
              </Button>
            ) : null}

            {state === 'ASSIGNED' ? (
              <Button
                size="small"
                loading={running}
                onClick={() =>
                  void perform(() => unassignEquipment(equipment.id), t('inventory.unassignDone'))
                }
              >
                {t('inventory.unassign')}
              </Button>
            ) : null}

            {state === 'FAULTY' ? (
              <Button
                size="small"
                type="primary"
                loading={running}
                onClick={() =>
                  void perform(() => repairEquipment(equipment.id), t('inventory.repairDone'))
                }
              >
                {t('inventory.repair')}
              </Button>
            ) : null}

            {/*
              «Topilgan deb belgilash» — TASDIQSIZ va bu ataylab: amal
              yo'qotishni QAYTARADI, jihoz omborga tushadi. Uni tasdiq
              oynasi ortiga yashirish har bir bezarar amalni ham
              "o'ylab ko'ring" deb ko'rsatardi va tasdiq oynasining
              o'zi arzonlashardi — chindan xavflisi ham shunday
              bosib o'tiladigan bo'lardi.
            */}
            {canMarkFound ? (
              <Button
                size="small"
                type="primary"
                loading={running}
                onClick={() =>
                  void perform(() => foundEquipment(equipment.id), t('inventory.foundDone'))
                }
              >
                {t('inventory.markFound')}
              </Button>
            ) : null}
          </Space>
        </Can>
      )}

      {state !== 'DECOMMISSIONED' && (canMarkFaulty || canMarkLost || canDecommission) ? (
        <Can permission="inventory.equipment.write">
          <Divider plain orientation="left" style={{ marginTop: 20, marginBottom: 12 }}>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {t('inventory.dangerGroup')}
            </Typography.Text>
          </Divider>

          <Space wrap>
            {/*
              ⚠️ `LOST` da "nosoz deb belgilash" YO'Q. Ilgari bu shart
              `state === 'FAULTY' ? ta'mirlash : nosoz` ko'rinishida edi, ya'ni
              `FAULTY` dan boshqa HAR holatda nosoz tugmasi chiqardi. `LOST`
              ro'yxatga qo'shilgach (`equipmentApi` izohi) bu tugma yo'qolgan
              jihozda ham paydo bo'lardi — server esa `LOST -> FAULTY` o'tishini
              bilmaydi va uni rad etardi.
            */}
            {canMarkFaulty ? (
              <Button size="small" danger loading={running} onClick={() => setFaultyOpen(true)}>
                {t('inventory.markFaulty')}
              </Button>
            ) : null}

            {/*
              «Yo'qolgan deb belgilash» — nosozlik bilan YONMA-YON, va bu
              to'g'ri: ikkalasi ham yomon xabar va ikkalasidan ham qaytish
              bor (`repair` / `found`). Ikkalasi ham oyna ochadi va sabab
              so'raydi, ya'ni tasodifan bosilgani OYNADA to'xtaydi.
              Yakuniy amal esa pastda, alohida qatorda (fayl sarlavhasi).
            */}
            {canMarkLost ? (
              <Button size="small" danger loading={running} onClick={() => setLostOpen(true)}>
                {t('inventory.markLost')}
              </Button>
            ) : null}
          </Space>

          {/* Hisobdan chiqarish FAQAT egasiz jihoz uchun: biriktirilgan
              qurilmani hisobdan chiqarish uni abonentda qoldirib, hisobda
              esa yo'q qilardi — inventarning ma'nosi shu yerda yo'qolardi.
              ⚠️ `IN_STOCK` dan ham to'g'ridan-to'g'ri o'tib bo'lmaydi
              (`EquipmentState` javadoc'i: avval `FAULTY` yoki `LOST` orqali,
              ya'ni sabab har doim qayd etiladi).
              ⚠️ ALOHIDA QATORDA — fayl sarlavhasidagi 4-band. */}
          {canDecommission ? (
            <div style={{ marginTop: canMarkFaulty || canMarkLost ? 12 : 0 }}>
              <Button size="small" danger type="primary" loading={running} onClick={confirmDecommission}>
                {t('inventory.decommission')}
              </Button>
            </div>
          ) : null}
        </Can>
      ) : null}

      <FaultyModal
        open={faultyOpen}
        equipment={equipment}
        busy={running}
        onCancel={() => setFaultyOpen(false)}
        onSubmit={(reason) => {
          setFaultyOpen(false)
          void perform(
            () => markEquipmentFaulty(equipment.id, reason),
            t('inventory.markFaultyDone'),
          )
        }}
      />

      <LostModal
        open={lostOpen}
        equipment={equipment}
        onClose={() => setLostOpen(false)}
        onDone={(outcome) => {
          setLostOpen(false)
          report(outcome, t('inventory.markLostDone'))
        }}
      />
    </>
  )
}

interface LostModalProps {
  open: boolean
  equipment: Equipment
  onClose: () => void
  onDone: (outcome: OperationResult | null) => void
}

/**
 * «Yo'qolgan deb belgilash» — tasdiq va sabab.
 *
 * <h2>Nega oyna, oddiy `modal.confirm` emas</h2>
 * Sabab MAJBURIY (`markEquipmentLost` izohi), ya'ni bu yerda kiritish
 * maydoni bor va oyna baribir kerak. Oynaning o'zi tasdiq vazifasini
 * bajaradi: sarlavha nima bo'layotganini aytadi, seriya raqami ko'rinib
 * turadi, tugma esa qizil.
 *
 * ⚠️ Terib tasdiqlash (`StockMovementModal` dagi ombor kodi) ATAYLAB
 * qo'shilmadi. U yerda sabab boshqa va u backend javadoc'ida yozilgan:
 * miqdorli molda "20 metr kamaydi" ni hech kim urishtira olmaydi, shuning
 * uchun u alohida xavfli huquq (`inventory.stock.adjust`) ostida. Seriyali
 * jihoz esa NUSXA bo'yicha hisobga olinadi va "yo'qoldi" tekshirilishi
 * mumkin — jihoz jismonan bor yoki yo'q. Har amalni terib tasdiqlatish
 * tasdiqning o'zini arzonlashtiradi.
 *
 * <h2>⚠️ Xato OYNANING ICHIDA</h2>
 * `onError` YO'Q va bo'lmaydi ham: xato mutatsiyada qoladi va shu yerda,
 * oynaning ichida ko'rsatiladi. Bu 2026-08-13 da 34 joyda tuzatilgan
 * tizimli nuqson edi — xato sahifa banneriga yozilsa, u modal niqobi
 * ORTIDA qolardi va operator faqat "bosdim, hech narsa bo'lmadi" ni
 * ko'rardi. Yopishda `reset()` — aks holda eski xato keyingi ochilishda
 * begona bo'lib chiqadi. Qorovul: `tests/guards.mjs` 1-bo'lim.
 */
function LostModal({ open, equipment, onClose, onDone }: LostModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ reason: string }>()

  const markLost = useMutation({
    mutationFn: (reason: string) => markEquipmentLost(equipment.id, reason),
    onSuccess: onDone,
  })

  const close = () => {
    markLost.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('inventory.markLostTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => markLost.mutate(values.reason.trim()))}
      confirmLoading={markLost.isPending}
      okText={t('inventory.markLost')}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: true }}
      destroyOnHidden
    >
      {markLost.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(markLost.error, t)}
        />
      ) : null}

      <Typography.Paragraph type="secondary">
        {t('inventory.markLostSubject', { serial: equipment.serialNumber })}
      </Typography.Paragraph>

      {/* ⚠️ Egasi bo'lgan jihozda oqibat BITTA emas, IKKITA: holat ham
          o'zgaradi, ega ham tozalanadi. Buni keyin emas, OLDIN aytamiz —
          `EquipmentOwnerModal` dagi ikki qadamli ogohlantirish bilan bir
          xil mulohaza. */}
      {equipment.state === 'ASSIGNED' ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('inventory.markLostOwnerWarnTitle')}
          description={<ExpandableHint text={t('inventory.markLostOwnerWarnBody')} fontSize={13} />}
        />
      ) : null}

      <Form form={form} layout="vertical" preserve={false} disabled={markLost.isPending}>
        <Form.Item
          name="reason"
          label={t('inventory.lostReason')}
          extra={t('inventory.lostReasonHint')}
          rules={[{ required: true, message: t('inventory.validation.lostReasonRequired') }]}
        >
          <Input.TextArea rows={3} maxLength={300} showCount autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  )
}

interface FaultyModalProps {
  open: boolean
  equipment: Equipment
  busy: boolean
  onCancel: () => void
  onSubmit: (reason: string) => void
}

/**
 * Nosozlik sababi.
 *
 * ⚠️ Sabab MAJBURIY. "Buzilgan" degan holatning o'zi hech narsa aytmaydi:
 * ta'mirga yuborayotgan usta ham, keyin hisobot ko'rayotgan boshliq ham
 * NIMASI buzilganini bilishi kerak. Bo'sh sabab bu yozuvni foydasiz qilardi.
 */
function FaultyModal({ open, equipment, busy, onCancel, onSubmit }: FaultyModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ reason: string }>()

  return (
    <Modal
      open={open}
      title={t('inventory.markFaultyTitle')}
      onCancel={onCancel}
      onOk={() => submitOnValid(form, (values) => onSubmit(values.reason.trim()))}
      confirmLoading={busy}
      okText={t('inventory.markFaulty')}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: true }}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        {t('inventory.markFaultySubject', { serial: equipment.serialNumber })}
      </Typography.Paragraph>
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="reason"
          label={t('inventory.faultReason')}
          extra={t('inventory.faultReasonHint')}
          rules={[{ required: true, message: t('inventory.validation.reasonRequired') }]}
        >
          <Input.TextArea rows={3} maxLength={300} showCount autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  )
}
